import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  LayoutDashboard, Building2, Users, GraduationCap,
  ClipboardCheck, Award, ListTodo, Menu, X, FileText,
  Calendar as CalendarIcon, CreditCard, Target, RefreshCcw, BookOpen, FileCheck, Settings, BookMarked, FolderCheck, FileSignature, TrendingUp, History, Phone, Shield,
  Umbrella, BarChart3, Wand2, Wallet, UserPlus, FolderTree, Landmark
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const ROLLEN = { gf: "Geschäftsführerin", verwaltung: "Verwaltung", dozent: "Dozentin", vertrieb: "Vertrieb" };

const Sidebar = ({ collapsed, setCollapsed, userRole }) => {
  const { user } = useAuth();
  const userName = user?.name || "";
  const initialen = userName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const navigate = useNavigate();
  const location = useLocation();
  const [ohneKursCount, setOhneKursCount] = useState(0);
  const [ueberfaelligCount, setUeberfaelligCount] = useState(0);

  // Anzahl TN ohne Kurs für Sidebar-Badge laden (alle 60s)
  useEffect(() => {
    if (!['gf', 'verwaltung'].includes(userRole)) return;
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/azav-mappe/ohne-kurs`)
        .then(r => { if (!cancelled) setOhneKursCount(r.data?.total || 0); })
        .catch(() => {});
      axios.get(`${API}/rechnungen/statistik`)
        .then(r => { if (!cancelled) setUeberfaelligCount(r.data?.ueberfaellig || 0); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [userRole]);

  // Alle Menüpunkte mit Rollen-Berechtigung
  const allNavItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/", roles: ['gf', 'verwaltung', 'dozent', 'vertrieb'] },
    { icon: CalendarIcon, label: "Kalender", path: "/kalender", roles: ['gf', 'verwaltung', 'dozent', 'vertrieb'] },
    { icon: Phone, label: "Telefonberatungen", path: "/termine", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: CalendarIcon, label: "Betriebsferien", path: "/betriebsferien", roles: ['gf', 'verwaltung'] },
    { icon: Phone, label: "Dozenten", path: "/dozenten", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: Building2, label: "Pflegeeinrichtungen", path: "/pflegeeinrichtungen", roles: ['gf', 'verwaltung', 'vertrieb', 'dozent'] },
    { icon: TrendingUp, label: "Vertrieb & Provisionen", path: "/vertrieb-auswertung", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: Users, label: "Teilnehmer", path: "/teilnehmer", roles: ['gf', 'verwaltung', 'dozent', 'vertrieb'] },
    { icon: FolderCheck, label: "QM-Cockpit", path: "/qm-cockpit", roles: ['gf', 'verwaltung'] },
    { icon: FolderCheck, label: "AZAV-Mappe", path: "/azav-mappe", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: FolderTree, label: "AZAV-Ordner", path: "/azav-ordner", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: FileCheck, label: "AZAV-Dokumente", path: "/azav-dokumente", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: Wand2, label: "AZAV-Antrags-Wizard", path: "/azav-antrags-wizard", roles: ['gf', 'verwaltung'] },
    { icon: BarChart3, label: "Verbleibsbefragung", path: "/verbleibsbefragung", roles: ['gf', 'verwaltung'] },
    { icon: Users, label: "Personalakten", path: "/personalakten", roles: ['gf', 'verwaltung'] },
    { icon: BookMarked, label: "Klassenbuch", path: "/klassenbuch-neu", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: BookMarked, label: "Lückenliste Klassenbuch", path: "/klassenbuch-luecken", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: Settings, label: "BG-Einstellungen", path: "/bg-kurs-einstellungen", roles: ['gf', 'verwaltung'] },
    { icon: FileSignature, label: "BG-Angebote", path: "/bg-angebote", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: FileText, label: "Rechnungen", path: "/rechnungen", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: CreditCard, label: "Ratenzahlung", path: "/ratenzahlung", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: FileText, label: "Eingangsrechnungen", path: "/eingangsrechnungen", roles: ['gf', 'verwaltung'] },
    { icon: BookOpen, label: "Ausgangsbuch & Journal", path: "/rechnungsbuecher", roles: ['gf', 'verwaltung'] },
    { icon: Landmark, label: "Kontoauszüge", path: "/kontoauszuege", roles: ['gf', 'verwaltung'] },
    { icon: FileCheck, label: "E-Rechnung & Versand", path: "/erechnung-versand", roles: ['gf', 'verwaltung'] },
    { icon: TrendingUp, label: "Finanzübersicht", path: "/finanzuebersicht", roles: ['gf', 'verwaltung'] },
    { icon: Wallet, label: "Buchhaltung", path: "/buchhaltung", roles: ['gf', 'verwaltung'] },
    { icon: GraduationCap, label: "Kurstermine & Belegung", path: "/kurstermine", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: GraduationCap, label: "Maßnahmen", path: "/massnahmen", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: ClipboardCheck, label: "Prüfungen", path: "/pruefungen", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: Award, label: "Zertifikate", path: "/zertifikate", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: Award, label: "Bestanden ohne Zertifikat", path: "/pruefungen?filter=zert", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: ClipboardCheck, label: "Prüfung nicht hochgeladen", path: "/pruefungen?filter=pr", roles: ['gf', 'verwaltung', 'dozent'] },
    { icon: ListTodo, label: "Aufgaben", path: "/aufgaben", roles: ['gf', 'verwaltung', 'vertrieb'] },
    { icon: History, label: "Änderungsprotokoll", path: "/aktivitaeten", roles: ['gf', 'verwaltung', 'dozent', 'vertrieb'] },
    { icon: Settings, label: "Datensicherung & Wartung", path: "/datensicherung-wartung", roles: ['gf', 'verwaltung'] },
    { icon: Umbrella, label: "Urlaubsplanung", path: "/urlaub", roles: ['gf', 'verwaltung', 'dozent', 'vertrieb'] },
    { icon: RefreshCcw, label: "Ninox Sync", path: "/ninox-sync", roles: ['gf', 'verwaltung'] },
  ];

  // 13 Bereiche; jeder bisherige Menüpunkt bleibt als Unterpunkt erreichbar
  const GRUPPEN = [
    { label: "Dashboard", icon: LayoutDashboard, paths: ["/"] },
    { label: "Kalender & Urlaub", icon: CalendarIcon, paths: ["/kalender", "/termine", "/urlaub", "/betriebsferien"] },
    { label: "Teilnehmer", icon: Users, paths: ["/teilnehmer"] },
    { label: "Pflegeeinrichtungen", icon: Building2, paths: ["/pflegeeinrichtungen", "/vertrieb-auswertung"] },
    { label: "Klassenbuch", icon: BookMarked, paths: ["/klassenbuch-neu", "/klassenbuch-luecken"] },
    { label: "Prüfungen & Zertifikate", icon: ClipboardCheck, paths: ["/pruefungen", "/zertifikate", "/pruefungen?filter=zert", "/pruefungen?filter=pr"] },
    { label: "Maßnahmen & BG", icon: GraduationCap, paths: ["/kurstermine", "/massnahmen", "/bg-kurs-einstellungen", "/bg-angebote"] },
    { label: "AZAV & QM", icon: FolderCheck, paths: ["/qm-cockpit", "/azav-mappe", "/azav-ordner", "/azav-dokumente", "/azav-antrags-wizard", "/verbleibsbefragung"] },
    { label: "Finanzen", icon: TrendingUp, paths: ["/rechnungen", "/ratenzahlung", "/eingangsrechnungen", "/rechnungsbuecher", "/kontoauszuege", "/erechnung-versand", "/finanzuebersicht", "/buchhaltung"] },
    { label: "Team", icon: Phone, paths: ["/dozenten", "/personalakten"] },
    { label: "Aufgaben", icon: ListTodo, paths: ["/aufgaben"] },
    { label: "System", icon: RefreshCcw, paths: ["/aktivitaeten", "/datensicherung-wartung", "/ninox-sync"] },
  ];
  const itemByPath = Object.fromEntries(allNavItems.filter(i => i.roles.includes(userRole)).map(i => [i.path, i]));
  const gruppen = GRUPPEN.map(g => ({ ...g, items: g.paths.map(p => itemByPath[p]).filter(Boolean) })).filter(g => g.items.length);
  const istAktiv = (path) => path.includes('?') ? location.pathname + location.search === path : location.pathname === path && !location.search.includes('filter=');
  const [offen, setOffen] = useState(() => Object.fromEntries(GRUPPEN.map(g => [g.label, g.paths.some(p => p.split('?')[0] === location.pathname)])));
  const testid = (path) => `nav-${path.replace('/', '').replace('?filter=', '-') || 'dashboard'}`;
  const badgeFuer = (path) => path === '/azav-mappe' ? ohneKursCount : path === '/rechnungen' ? ueberfaelligCount : 0;
  const go = (path) => { navigate(path); if (window.innerWidth < 768) setCollapsed(true); };

  return (
    <>
    <button onClick={() => setCollapsed(false)} className="mobile-menu-btn" aria-label="Menü öffnen" data-testid="mobile-menu-open"><Menu size={20} /></button>
    {!collapsed && <div className="mobile-backdrop" onClick={() => setCollapsed(true)} data-testid="mobile-menu-backdrop" />}
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} flex flex-col`} data-testid="sidebar">
      <div className="px-5 py-6 border-b flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D9775A] flex items-center justify-center overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_educare-admin/artifacts/a9evupkl__DH_Icon.jpeg" alt="Logo" className="w-10 h-10 object-cover" />
            </div>
            <div>
              <div className="text-[15px] font-extrabold tracking-tight text-white leading-tight">Dickies-Campus</div>
              <div className="text-[10px] tracking-[0.18em] text-[#D9775A] uppercase mt-0.5 font-semibold">Akademie Cockpit</div>
            </div>
          </div>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 hover:bg-[#23211F] rounded-full text-[#8C867E] hover:text-white transition-colors" data-testid="sidebar-toggle">
          {collapsed ? <Menu size={18} /> : <X size={18} />}
        </button>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5">
        {gruppen.map((g) => {
          const einzeln = g.items.length === 1;
          const aktiv = g.items.some(i => istAktiv(i.path));
          const summe = g.items.reduce((n, i) => n + badgeFuer(i.path), 0);
          const klick = () => einzeln || collapsed ? go(g.items[0].path) : setOffen(o => ({ ...o, [g.label]: !o[g.label] }));
          return (
            <div key={g.label}>
              <button onClick={klick} title={g.label} className={`nav-item w-full ${aktiv && (einzeln || collapsed || !offen[g.label]) ? 'active' : ''}`}
                data-testid={einzeln ? testid(g.items[0].path) : `nav-gruppe-${g.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <g.icon size={16} />
                {!collapsed && (
                  <span className="flex items-center justify-between flex-1">
                    <span>{g.label}</span>
                    <span className="flex items-center gap-1.5">
                      {summe > 0 && <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-[#D9775A] rounded-full inline-flex items-center justify-center">{summe}</span>}
                      {!einzeln && <span className={`text-[10px] transition-transform ${offen[g.label] ? 'rotate-90' : ''}`}>›</span>}
                    </span>
                  </span>
                )}
              </button>
              {!collapsed && !einzeln && offen[g.label] && g.items.map((i) => {
                const b = badgeFuer(i.path);
                return (
                  <button key={i.path} onClick={() => go(i.path)} className={`nav-sub ${istAktiv(i.path) ? 'active' : ''}`} data-testid={testid(i.path)}
                    title={b ? (i.path === '/azav-mappe' ? `${b} Teilnehmer ohne Kurszuordnung` : `${b} überfällige Rechnung(en)`) : i.label}>
                    <span>{i.label}</span>
                    {b > 0 && <span className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#D9775A] rounded-full inline-flex items-center justify-center"
                      data-testid={i.path === '/azav-mappe' ? 'sidebar-azav-badge' : 'sidebar-rechnungen-badge'}>{b}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
      {!collapsed && (
        <div className="px-4 py-4 border-t flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#D9775A] text-white flex items-center justify-center font-bold text-xs">
            {initialen}
          </div>
          <div className="text-xs leading-tight" data-testid="sidebar-user">
            <div className="font-semibold text-white">{userName}</div>
            <div className="text-[#8A7F70] text-[10px] mt-0.5">{userRole === "gf" && /^(markus|jean)@/i.test(user?.email || "") ? "Geschäftsführer" : (ROLLEN[userRole] || userRole)} · Dickies-Campus GmbH</div>
          </div>
        </div>
      )}
    </aside>
    </>
  );
};

export default Sidebar;
